import type {
  AgentTraits,
  AnalyticsAdapter,
  AnalyticsConfig,
  AnalyticsPageViewProperties,
  AnalyticsTrackProperties,
} from './analyticsAdapter.ts'
import {
  type AnalyticsLog,
  type AnalyticsTestBridge,
  INTERNAL_TOOLS_ANALYTICS_TEST_BRIDGE_WINDOW_KEY,
} from './analyticsTestBridge.ts'

export class MockAnalyticsAdapter implements AnalyticsAdapter {
  private logs: AnalyticsLog[] = []

  private findLastLog(
    predicate: (log: AnalyticsLog) => boolean,
  ): AnalyticsLog | undefined {
    for (let i = this.logs.length - 1; i >= 0; i--) {
      const log = this.logs[i]
      if (log && predicate(log)) return log
    }
    return undefined
  }

  initialize(_config: AnalyticsConfig): void {
    // Expose bridge to window for Playwright access (mock mode only).
    if (typeof window === 'undefined') return

    window[INTERNAL_TOOLS_ANALYTICS_TEST_BRIDGE_WINDOW_KEY] = {
      getLogs: () => [...this.logs],
      clearLogs: () => {
        this.logs = []
      },
      findEvent: (name: string) => {
        return this.findLastLog((l) => l.type === 'track' && l.event === name)
      },
      findAllEvents: (name: string) => {
        return this.logs.filter((l) => l.type === 'track' && l.event === name)
      },
      // Return the most recent identify for the agent (more useful than the first one).
      findIdentify: (agentId: string) => {
        return this.findLastLog(
          (l) => l.type === 'identify' && l.agentId === agentId,
        )
      },
      findAllIdentifies: (agentId: string) => {
        return this.logs.filter(
          (l) => l.type === 'identify' && l.agentId === agentId,
        )
      },
      findPageView: (screenName: string) => {
        return this.findLastLog(
          (item) => item.type === 'pageView' && item.screenName === screenName,
        )
      },
      findAllPageViews: (screenName: string) => {
        return this.logs.filter(
          (item) => item.type === 'pageView' && item.screenName === screenName,
        )
      },
    } satisfies AnalyticsTestBridge
  }

  track(eventName: string, properties?: AnalyticsTrackProperties): void {
    this.logs.push({
      type: 'track',
      event: eventName,
      properties,
      timestamp: Date.now(),
    })
  }

  pageView(screenName: string, properties?: AnalyticsPageViewProperties): void {
    this.logs.push({
      type: 'pageView',
      screenName,
      properties,
      timestamp: Date.now(),
    })
  }

  identify(agentId: string, traits?: AgentTraits): void {
    this.logs.push({
      type: 'identify',
      agentId,
      properties: traits,
      timestamp: Date.now(),
    })
  }

  reset(): void {
    this.logs = []
  }
}
